import { WORMHOLE_ADAPTER_ABI } from "@/config/abis/wormhole-adapter";
import { BRIDGE_RESILIENT_1155_ABI } from "@/config/abis/bridge-resilient-1155";
import {
  ARBITRUM_CHAIN_ID,
  ARBITRUM_WORMHOLE,
  BLESSNET_CHAIN_ID,
  GAS_LIMIT,
} from "@/features/bridge/constants/bridge.constant";
import { writeContract } from "wagmi/actions";
import { useConfig, useWalletClient, usePublicClient } from "wagmi";
import { getContract } from "viem";
import { ethers } from "ethers";
import type { ChainData } from "@/constants/chain-details";
import type useBridgeControls from "@/features/bridge/hooks/useBridgeControls";
import { log } from "@/features/bridge/utils/log";
import { calculateGas } from "@/utils/calculate-gas.util";
import {
  showToast,
  showToastTxRevert,
  showToastTxSuccess,
} from "@components/layout/toast/show-toast";
import { TOAST_TX_MSG } from "@/features/bridge/constants/toast-msgs.constants";
import { erc20Abi, erc721Abi } from "viem";
import type { HexString } from "@/types/general.types";
import { CONTRACT_ADDRESSES } from "@/constants/contract-addresses.constants";
import { wormholeExecutorOnChainQuoterKey } from "@/constants/flags.constant";
import { useManagedWriteContract } from "@/hooks/useManagedContractWrite.hook";
import { getChainClient } from "@/utils/get-chain-client.util";
import { getExecutorSignedQuote } from "@/features/bridge/services/wormhole/getExecutorSignedQuote";
import { encodeGasRelayInstruction } from "@/features/bridge/services/wormhole/relayInstructions";
import {
  encodeBridgeRoutingConfig,
  encodeWormholeRouteData,
} from "@/features/bridge/services/wormhole/routeData";
import { isParentChildRoute } from "@/features/bridge/utils/isParentChildRoute.util";

type BridgeControls = ReturnType<typeof useBridgeControls>;

/**
 * A custom hook that provides various utility methods for interacting with assets
 * across different blockchain networks. These methods include checking token shiftability,
 * retrieving allowances, managing approvals, and performing asset transfers (shifts)
 * between chains using ERC20, ERC721, and ERC1155 standards.
 */
export const useGeneralAssetsMethods = (bridgeControls: BridgeControls) => {
  const isQuoteExpiredError = (error: unknown) =>
    error instanceof Error && /expired|expiry/i.test(error.message);
  const getReadableQuoteErrorMessage = (error: unknown) =>
    error instanceof Error && error.message
      ? error.message
      : TOAST_TX_MSG.EXECUTOR_QUOTE_ERROR.toastContent.message;

  const activeConfig = useConfig();
  usePublicClient();
  useWalletClient();

  const { writeContractAsyncWithTimeout } = useManagedWriteContract({
    callback: {
      onSigningError: () => {
        showToast(TOAST_TX_MSG.SIGN_ERROR);
      },
      onTimeout: () =>
        showToast({
          toastContent: TOAST_TX_MSG.TX_SUCCESS_TIMEOUT.toastContent,
        }),
      onRevert: showToastTxRevert,
      onSuccess: (receiptHash) => showToastTxSuccess(receiptHash),
    },
  });

  // =================
  // Contract READS
  // =================

  //$ CHECK IF SHIFTABLE
  async function isShiftable(tokenAddress: HexString) {
    const { fromChain: { originChain } } = bridgeControls;

    if (!originChain?.chainId) {
      throw new Error("Origin chain not selected");
    }

    try {
      console.log(`🔍 Checking isShiftable via API for bridging: ${tokenAddress} on chain ${originChain.chainId}`);

      const response = await fetch(
        `/api/token-eligibility/${originChain.chainId}/${tokenAddress}`
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error(`API error for token ${tokenAddress}:`, errorData);
        throw new Error(`Failed to check token shiftability: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.cached) {
        console.log(`✅ Server cache hit for bridging token ${tokenAddress}: ${data.isShiftable}`);
      } else {
        console.log(`🔍 Server checked bridging token ${tokenAddress}: ${data.isShiftable}`);
      }

      return data.isShiftable;
    } catch (error) {
      console.error(`Failed to check token shiftability for ${tokenAddress}:`, error);
      throw error; // Re-throw for bridging operations to handle appropriately
    }
  }

  //$ GET ALLOWANCE FOR ERC20
  async function getAllowanceForERC20(
    tokenAddress: HexString,
    ownerAddress?: HexString,
  ) {
    if (!ownerAddress) throw new Error("Owner address is required");

    const { fromChain: { originChain } } = bridgeControls;

    if (!originChain?.chainId) {
      throw new Error("Origin chain not selected");
    }

    // Use the origin chain client instead of the wallet's connected chain
    const originChainClient = getChainClient(originChain.chainId);
    if (!originChainClient) {
      throw new Error(`Cannot create client for origin chain ID: ${originChain.chainId}`);
    }

    const contract = getContract({
      abi: erc20Abi,
      address: tokenAddress,
      client: originChainClient,
    });
    return contract.read.allowance([
      ownerAddress,
      CONTRACT_ADDRESSES.WORMHOLE_ADAPTER_1155,
    ]) as Promise<bigint>;
  }

      //$ GET APPROVAL FOR MULTI TOKEN
  async function getMultiTokenApprovalToBridge(
    contractAddress: HexString,
    walletOwner: HexString,
  ) {
    const { fromChain: { originChain } } = bridgeControls;

    if (!originChain?.chainId) {
      throw new Error("Origin chain not selected");
    }

    // Use the origin chain client instead of the wallet's connected chain
    const originChainClient = getChainClient(originChain.chainId);
    if (!originChainClient) {
      throw new Error(`Cannot create client for origin chain ID: ${originChain.chainId}`);
    }

    const contract = getContract({
      abi: erc721Abi,
      address: contractAddress,
      client: originChainClient,
    });
    return contract.read.isApprovedForAll([
      walletOwner,
      CONTRACT_ADDRESSES.WORMHOLE_ADAPTER_1155,
    ]) as Promise<boolean>;
  }

  const prepareWormholeRoutingConfig = async (
    destChain: ChainData,
    gasLimit: bigint,
    tokenAddress: HexString,
    shouldSendToBless: boolean = false,
  ) => {
    const { fromChain: { originChain } } = bridgeControls;

    if (!originChain?.chainId) {
      throw new Error("Origin chain not selected");
    }

    // Use the origin chain client instead of the wallet's connected chain
    const originChainClient = getChainClient(originChain.chainId);
    if (!originChainClient) {
      throw new Error(`Cannot create client for origin chain ID: ${originChain.chainId}`);
    }

    const tokenContract = getContract({
      abi: BRIDGE_RESILIENT_1155_ABI,
      address: tokenAddress,
      client: originChainClient,
    });

    try {
      if (!bridgeControls.account.address) {
        throw new Error("Missing wallet address for refund");
      }

      const bridgeAdapter = (await tokenContract.read.defaultBridge()) as HexString;
      if (!bridgeAdapter || /^0x0{40}$/i.test(bridgeAdapter)) {
        throw new Error("Token has no default bridge adapter configured");
      }

      const isParentToChildViaRemappedDestination =
        shouldSendToBless && originChain.chainId === ARBITRUM_CHAIN_ID;

      if (
        isParentChildRoute(originChain.chainId, destChain.chainId) ||
        isParentToChildViaRemappedDestination
      ) {
        const routeData = encodeWormholeRouteData({
          gasLimit,
          executorCost: BigInt(0),
          refundAddress: bridgeControls.account.address as HexString,
          signedQuote: "0x" as HexString,
        });

        return {
          wormHoleFee: BigInt(0),
          routingConfig: encodeBridgeRoutingConfig(
            bridgeAdapter,
            routeData,
          ),
        };
      }

      const sourceWormholeId =
        originChain.chainId === BLESSNET_CHAIN_ID
          ? ARBITRUM_WORMHOLE
          : originChain.wormholeId;

      const destinationWormholeId = shouldSendToBless
        ? ARBITRUM_WORMHOLE
        : destChain.wormholeId;

      if (!sourceWormholeId || !destinationWormholeId) {
        throw new Error("Missing source or destination wormhole id");
      }

      const relayInstructions = encodeGasRelayInstruction(gasLimit, BigInt(0));
      const executorQuote = await getExecutorSignedQuote({
        srcChain: sourceWormholeId,
        dstChain: destinationWormholeId,
        relayInstructions,
      });

      const routingSignedQuote =
        originChain.chainId === BLESSNET_CHAIN_ID
          ? (wormholeExecutorOnChainQuoterKey as HexString)
          : (executorQuote.signedQuote as HexString);

      const routeData = encodeWormholeRouteData({
        gasLimit,
        executorCost: executorQuote.estimatedCost,
        refundAddress: bridgeControls.account.address as HexString,
        signedQuote: routingSignedQuote,
      });

      let txValue = executorQuote.estimatedCost;
      if (originChain.chainId === BLESSNET_CHAIN_ID) {
        const adapterContract = getContract({
          abi: WORMHOLE_ADAPTER_ABI,
          address: bridgeAdapter,
          client: originChainClient,
        });
        txValue = (await adapterContract.read.quoteNativeForDelivery([
          destChain.chainId,
          routeData,
        ])) as bigint;
      }

      return {
        wormHoleFee: txValue,
        routingConfig: encodeBridgeRoutingConfig(
          bridgeAdapter,
          routeData,
        ),
      };
    } catch (error) {
      if (isQuoteExpiredError(error)) {
        showToast(TOAST_TX_MSG.EXECUTOR_QUOTE_EXPIRED);
      } else {
        console.error("Executor quote failed while preparing bridge route", {
          originChainId: originChain?.chainId,
          destinationChainId: destChain?.chainId,
          shouldSendToBless,
          error,
        });
        showToast({
          toastContent: {
            ...TOAST_TX_MSG.EXECUTOR_QUOTE_ERROR.toastContent,
            message: getReadableQuoteErrorMessage(error),
          },
        });
      }
      throw error;
    }
  }

  //$ RETRIEVE WORMHOLE QUOTE
  async function getWormHoleFeeQuote(
    destChain: ChainData,
    tokenAddress: HexString,
    gasLimit: bigint,
    shouldSendToBless: boolean,
  ) {
    const routing = await prepareWormholeRoutingConfig(
      destChain,
      gasLimit,
      tokenAddress,
      shouldSendToBless,
    );
    return routing.wormHoleFee;
  }

  // =================
  // Contract WRITES
  // =================

  //$ SHIFTOUT ERC20
  async function shiftOutShiftableERC20(
    destChain: ChainData,
    tokenAddress: HexString,
    amount: bigint,
    shouldSendToBless: boolean,
  ) {
    const { wormHoleFee, routingConfig } = await prepareWormholeRoutingConfig(
      destChain,
      GAS_LIMIT,
      tokenAddress,
      shouldSendToBless,
    );
    const PASS_ON_DATA = "0x" as HexString;
    const ERC20_TOKEN_ID = BigInt(0);

    // Log shift out call details
    console.log('🚀 SHIFT OUT CALL - shiftOutShiftableERC20:', {
      targetAddress: tokenAddress,
      destChain: {
        chainName: destChain.chainName,
        chainId: destChain.chainId,
        wormholeId: destChain.wormholeId
      },
      amount: amount.toString(),
      shouldSendToBless,
      wormHoleFee: wormHoleFee.toString(),
      gasLimit: GAS_LIMIT.toString(),
      routingConfig,
    });

    return await writeContractAsyncWithTimeout({
      abi: BRIDGE_RESILIENT_1155_ABI,
      address: tokenAddress,
      functionName: "bridgeOut",
      args: [
        BigInt(destChain.chainId),
        ERC20_TOKEN_ID,
        amount,
        PASS_ON_DATA,
        routingConfig,
      ],
      value: wormHoleFee,
    });
  }

  //$ SHIFT OUT ERC721
  async function shiftOutShiftableERC721(
    destChain: ChainData,
    tokenAddress: HexString,
    tokenIds: bigint[],
    shouldSendToBless: boolean,
  ) {
    const calculatedGas = calculateGas(GAS_LIMIT, BigInt(tokenIds.length));
    const { wormHoleFee, routingConfig } = await prepareWormholeRoutingConfig(
      destChain,
      calculatedGas,
      tokenAddress,
      shouldSendToBless,
    );
    const PASS_ON_DATA = "0x" as HexString;
    const amounts = tokenIds.map(() => BigInt(1));

    // Log shift out call details
    console.log('🚀 SHIFT OUT CALL - shiftOutShiftableERC721:', {
      targetAddress: tokenAddress,
      destChain: {
        chainName: destChain.chainName,
        chainId: destChain.chainId,
        wormholeId: destChain.wormholeId
      },
      tokenIds: tokenIds.map(id => id.toString()),
      shouldSendToBless,
      wormHoleFee: wormHoleFee.toString(),
      calculatedGas: calculatedGas.toString(),
      routingConfig,
    });

    return await writeContractAsyncWithTimeout({
      abi: BRIDGE_RESILIENT_1155_ABI,
      address: tokenAddress,
      functionName: "bridgeOutBatch",
      args: [
        BigInt(destChain.chainId),
        tokenIds,
        amounts,
        PASS_ON_DATA,
        routingConfig,
      ],
      value: wormHoleFee,
    });
  }

  //$ SHIFT OUT ERC1155
  async function shiftOutShiftableERC1155(
    destChain: ChainData,
    tokenAddress: HexString,
    tokenIds: bigint[],
    tokenAmounts: bigint[],
    shouldSendToBless: boolean,
  ) {
    const calculatedGas = calculateGas(GAS_LIMIT, BigInt(tokenIds.length));
    const { wormHoleFee, routingConfig } = await prepareWormholeRoutingConfig(
      destChain,
      calculatedGas,
      tokenAddress,
      shouldSendToBless,
    );
    const PASS_ON_DATA = "0x" as HexString;

    // Log shift out call details
    console.log('🚀 SHIFT OUT CALL - shiftOutShiftableERC1155:', {
      targetAddress: tokenAddress,
      destChain: {
        chainName: destChain.chainName,
        chainId: destChain.chainId,
        wormholeId: destChain.wormholeId
      },
      tokenIds: tokenIds.map(id => id.toString()),
      tokenAmounts: tokenAmounts.map(amount => amount.toString()),
      shouldSendToBless,
      wormHoleFee: wormHoleFee.toString(),
      calculatedGas: calculatedGas.toString(),
      routingConfig,
    });

    return await writeContractAsyncWithTimeout({
      abi: BRIDGE_RESILIENT_1155_ABI,
      address: tokenAddress,
      functionName: "bridgeOutBatch",
      args: [
        BigInt(destChain.chainId),
        tokenIds,
        tokenAmounts,
        PASS_ON_DATA,
        routingConfig,
      ],
      value: wormHoleFee,
    });
  }

  //$ HOLD AND SHIFT OUT ERC20
  async function holdAndShiftOut20(
    destChain: ChainData,
    contractAddress: HexString,
    amount: bigint,
    shouldSendToBless: boolean,
  ) {
    // Shift Gateway hold paths are deprecated; route through token shiftOut.
    return shiftOutShiftableERC20(
      destChain,
      contractAddress,
      amount,
      shouldSendToBless,
    );
  }

  //$ HOLD AND SHIFT OUT ERC721
  async function holdAndShiftOut721(
    destChain: ChainData,
    contractAddress: HexString,
    tokenIds: bigint[],
    shouldSendToBless: boolean,
  ) {
    // Shift Gateway hold paths are deprecated; route through token shiftOut.
    return shiftOutShiftableERC721(
      destChain,
      contractAddress,
      tokenIds,
      shouldSendToBless,
    );
  }

  //$ HOLD AND SHIFT OUT ERC1155
  async function holdAndShiftOut1155(
    destChain: ChainData,
    contractAddress: HexString,
    tokenIds: bigint[],
    tokenAmounts: bigint[],
    shouldSendToBless: boolean,
  ) {
    // Shift Gateway hold paths are deprecated; route through token shiftOut.
    return shiftOutShiftableERC1155(
      destChain,
      contractAddress,
      tokenIds,
      tokenAmounts,
      shouldSendToBless,
    );
  }

  //$ GET APPROVAL FOR STANDARD TOKEN
  async function setStandardTokenApprovalToBridge(contractAddress: HexString) {
    const maxUnsignedInt = ethers.MaxUint256; // Setting to max
    return await writeContract(activeConfig, {
      abi: erc20Abi,
      address: contractAddress,
      functionName: "approve",
      args: [CONTRACT_ADDRESSES.WORMHOLE_ADAPTER_1155, maxUnsignedInt],
    });
  }

  //$ SET APPROVAL FOR NFT/MULTI
  async function setMultiTokenApproval(
    contractAddress: HexString,
  ): Promise<HexString> {
    const shouldApprove = true;

    const txHash = await writeContractAsyncWithTimeout({
      abi: erc721Abi,
      address: contractAddress,
      functionName: "setApprovalForAll",
      args: [CONTRACT_ADDRESSES.WORMHOLE_ADAPTER_1155, shouldApprove],
    });

    return txHash;
  }

  //$ RESETS APPROVAL FOR NFT/MULTI
  async function resetApprovalForMultiTokens(
    contractAddress: HexString,
  ): Promise<HexString> {
    const shouldApprove = false;

    const txHash = await writeContractAsyncWithTimeout({
      abi: erc721Abi,
      address: contractAddress,
      functionName: "setApprovalForAll",
      args: [CONTRACT_ADDRESSES.WORMHOLE_ADAPTER_1155, shouldApprove],
    });

    return txHash;
  }

  return {
    isShiftable,
    getAllowanceForERC20,
    setStandardTokenApprovalToBridge,
    getMultiTokenApprovalToBridge,
    holdAndShiftOut20,
    holdAndShiftOut721,
    holdAndShiftOut1155,
    shiftOutShiftableERC20,
    shiftOutShiftableERC721,
    shiftOutShiftableERC1155,
    getWormHoleFeeQuote,
    setMultiTokenApproval,
    resetApprovalForMultiTokens,
  };
};
